import React from "react";
import {
  Typography,
  Box,
  Container,
  Paper,
  Grid,
  Avatar,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { 
  Check as CheckIcon,
  School as EducationIcon,
  Work as ExperienceIcon,
  EmojiObjects as PhilosophyIcon,
  People as TeamIcon,
  BusinessCenter as FirmIcon,
  Public as CommunityIcon,
  Brightness1 as BulletIcon,
  ExpandMore as ExpandMoreIcon,
  ContactMail as ContactIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from "@mui/icons-material";
import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const PageHeader = styled(Box)(({ theme }) => {
  // Get the theme URL dynamically
  const themeUrl = window.wpData?.themeUrl || '';
  
  return {
    backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${themeUrl}/build/grey-building.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
    padding: theme.spacing(10, 0, 6, 0),
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(2),
  };
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
}));



const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.75rem",
  marginBottom: theme.spacing(3),
  color: "#0e3158",
  position: "relative",
  paddingBottom: theme.spacing(1),
  textAlign: "center",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60px",
    height: "3px",
    backgroundColor: "#f0b94d",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.25rem",
  marginBottom: theme.spacing(2),
  color: "#0e3158",
  display: "flex",
  alignItems: "center",
  "& svg": {
    marginRight: theme.spacing(1),
  }
}));

const TeamMemberCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  overflow: "visible",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
}));

const ContentSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
}));

const TeamMemberName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.5rem",
  marginBottom: theme.spacing(0.5),
  color: "#0e3158",
}));

const TeamMemberTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(2),
  color: "#667085",
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(18),
  height: theme.spacing(18),
  marginBottom: theme.spacing(2),
  border: `3px solid ${theme.palette.background.paper}`,
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
}));

const MembershipItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.75, 0),
}));

const MembershipIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: "30px",
  color: "#f0b94d",
}));

const CtaSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: '#f5f7fa',
  borderRadius: '8px',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  color: '#0e3158',
}));

const ValueCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  borderRadius: '8px',
  boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
}));

const ValueIcon = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ValueTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  marginBottom: theme.spacing(1),
  color: '#0e3158',
}));

function AboutUs() {
  const professionalMemberships = [
    "Law Society of Alberta",
    "Nigerian Bar Association",
    "Canadian Bar Association",
    "Calgary Bar Association",
    "International Law Association – Canada"
  ];

  // Removed firm values section based on request

  return (
    <>
      <SeoMetaTags
        defaultTitle="About Us - Meet Our Team | Sentry Law"
        defaultDescription="Meet David Ike, founder and principal lawyer at Sentry Law. Over 15 years of legal experience across Canada, UK, and Nigeria. Expert in corporate law, personal injury, and more."
        slug="about-us"
      />
      {/* Header Section */}
      <PageHeader>
        <Container maxWidth="lg">
          <PageTitle variant="h1">
            Meet the Team
          </PageTitle>
        </Container>
      </PageHeader>

      <Container maxWidth="lg" sx={{ mb: 8 }}>

        {/* Meet Our Team Section */}
        <SectionTitle variant="h2" gutterBottom sx={{ mt: 6, mb: 4 }}>
          Meet Our Team
        </SectionTitle>

        {/* Team Member - David Ike */}
        <TeamMemberCard>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <LargeAvatar 
                  src={`${window.wpData?.themeUrl || ''}/build/Ike, David.jpg`} 
                  alt="David Ike" 
                  sx={(theme) => ({ 
                    objectPosition: "center 15%", 
                    objectFit: "cover",
                    width: theme.spacing(20),
                    height: theme.spacing(20)
                  })}>
                  DI
                </LargeAvatar>
                <TeamMemberName>David Ike</TeamMemberName>
                <TeamMemberTitle>Founder & Principal Lawyer</TeamMemberTitle>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  David Ike, Ph.D., LL.M., LL.B
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, fontWeight: 600 }}>
                  Principal Lawyer, Sentry Law
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  David Ike is the founder and principal lawyer at Sentry Law, bringing over 15 years of legal experience across Canada, the United Kingdom, and Nigeria. He is a member of the Law Society of Alberta, Canadian Bar association and is known for his thoughtful, client-focused approach and his ability to navigate complex legal matters with clarity and precision.
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  David is also a Chartered Arbitrator (CIArb) and a certified Project Management Professional (PMP), reflecting his commitment to interdisciplinary excellence as he serves individuals, families, and businesses across a wide range of legal needs.
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  At the heart of David's practice is a commitment to personalized service, practical solutions, and effective advocacy. He takes the time to understand each client's goals and delivers advice with empathy, professionalism, and clarity.
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  David is happily married and a proud father of two beautiful daughters.
                </Typography>
                
                {/* Accordion for Contact */}
                <Accordion sx={{ 
                  mt: 3, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', 
                  borderRadius: '8px',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { mt: 3 }
                }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      bgcolor: '#f5f7fa', 
                      borderRadius: '8px',
                      '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ContactIcon sx={{ color: '#0e3158', mr: 1 }} />
                      <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '1.25rem', color: '#0e3158' }}>
                        Contact
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ color: '#f0b94d', mr: 2 }} />
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          Phone: 403-971-7036
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ color: '#f0b94d', mr: 2 }} />
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          Email: david.ike@sentrylaw.ca
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </CardContent>
        </TeamMemberCard>

        {/* Team Member - Chinyere A. George */}
        <TeamMemberCard>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <LargeAvatar 
                  src={`${window.wpData?.themeUrl || ''}/build/colleague_obbi.jpg`}
                  alt="Chinyere A. George" 
                  sx={(theme) => ({ 
                    objectPosition: "center 15%", 
                    objectFit: "cover",
                    width: theme.spacing(20),
                    height: theme.spacing(20)
                  })}>
                  CG
                </LargeAvatar>
                <TeamMemberName>Chinyere A. George</TeamMemberName>
                <TeamMemberTitle>Para-Legal</TeamMemberTitle>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  Chinyere A. George, B.Sc.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, fontWeight: 600 }}>
                  Para-Legal, Sentry Law
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  Chinyere holds a B.Sc. graduate of Sociology with a strong background in human behavior, organizational structure, and communication. Chinyere brings a unique sociological perspective to the legal environment. Her career spans experience in client-focused service delivery, social media communication, and administrative support, with a track record of excellence in research, and digital communication. She is adept at handling documentation, client correspondence, and public-facing communication for legal practices.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </TeamMemberCard>

        {/* Core Values Section */}
        <ContentSection>
          <SectionTitle variant="h2" gutterBottom sx={{ mb: 4 }}>
            Core Values
          </SectionTitle>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 3 
          }}>
            {/* Integrity */}
            <ValueCard>
              <ValueIcon>
                <BulletIcon sx={{ color: '#0e3158' }} />
              </ValueIcon>
              <ValueTitle>Integrity</ValueTitle>
              <Typography variant="body2">
                We uphold the highest ethical standards, honesty, and transparency in all our dealings. Your trust is our most valued asset, and we earn it through accountability and sincerity.
              </Typography>
            </ValueCard>
            
            {/* Excellence */}
            <ValueCard>
              <ValueIcon>
                <BulletIcon sx={{ color: '#0e3158' }} />
              </ValueIcon>
              <ValueTitle>Excellence</ValueTitle>
              <Typography variant="body2">
                We are committed to legal excellence and meticulous attention to detail in every case. Our team continually updates its knowledge and skills to deliver top-tier representation.
              </Typography>
            </ValueCard>
            
            {/* Client Focus */}
            <ValueCard>
              <ValueIcon>
                <BulletIcon sx={{ color: '#0e3158' }} />
              </ValueIcon>
              <ValueTitle>Client Focus</ValueTitle>
              <Typography variant="body2">
                Your goals and concerns drive our approach. We listen actively and tailor our strategies to your unique needs, ensuring you are informed and involved at every stage.
              </Typography>
            </ValueCard>
            
            {/* Compassion & Respect */}
            <ValueCard>
              <ValueIcon>
                <BulletIcon sx={{ color: '#0e3158' }} />
              </ValueIcon>
              <ValueTitle>Compassion & Respect</ValueTitle>
              <Typography variant="body2">
                We understand that legal matters can be stressful. We treat every client with empathy, respect, and discretion, fostering a supportive environment where you feel heard and cared for.
              </Typography>
            </ValueCard>
          </Box>
        </ContentSection>
        {/* Firm's Purpose & Philosophy */}
        <ContentSection>
          <SectionTitle variant="h2" gutterBottom sx={{ mb: 4 }}>
          Community
          </SectionTitle>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
            We are proud to be a part of the Calgary community and take seriously our responsibility to give back. Community service and accessibility are important to us. Our team volunteers time and expertise to local legal clinics and nonprofit initiatives, aiming to improve access to justice for underrepresented groups. We also embrace diversity and inclusion within our office – Sentry Law is a place where different perspectives are celebrated, as we believe it makes us stronger advisors and advocates. By nurturing a culture of respect, empathy, and social responsibility, we not only create a great place to work but also a firm that understands and reflects the diverse clients we serve.
          </Typography>
        </ContentSection>

        {/* CTA Section */}
        <CtaSection>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700, color: '#0e3158' }}>
            Get Trusted Legal Advice Now
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            Contact us today to schedule a free consultation with one of our experienced attorneys.
            We're here to provide the guidance and representation you need.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact-us"
              sx={{ 
                bgcolor: '#f0b94d', 
                color: '#0e3158', 
                fontWeight: 600,
                '&:hover': { bgcolor: '#d9a843' }, 
                px: 3, 
                py: 1.5 
              }}
            >
              Schedule Free Consultation
            </Button>
          </Box>
        </CtaSection>
      </Container>
    </>
  );
}

export default AboutUs;